import React, { useState, useEffect } from 'react';
import { ExternalLink, AlertCircle } from 'lucide-react';
import FoerderMonitoringService from '@/services/FoerderMonitoringService';
import { useTranslation } from 'react-i18next';

const NewsLaufband = () => {
  const [news, setNews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation("dashboard");

  useEffect(() => {
    loadNews();
    const interval = setInterval(loadNews, 5 * 60 * 1000); // Alle 5 Minuten aktualisieren
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (news.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % news.length);
    }, 4000); // Schnelleres Wechseln: 4 Sekunden statt 6
    
    return () => clearInterval(interval);
  }, [news.length]);

  const loadNews = async () => {
    try {
      setLoading(true);
      const tickerNews = await FoerderMonitoringService.getTickerUpdates();
      
      // Erweiterte Filterung für mehr relevante News
      const validNews = tickerNews.filter(item => {
        // DEFENSIVE PROGRAMMIERUNG - Null-Checks
        if (!item || typeof item !== 'object') return false;
        
        // Link-Validierung (weniger restriktiv)
        const hasValidLink = item.link && item.link !== '#';
        
        // Aktualitäts-Check (max. 90 Tage alt statt 30)
        const isRecent = item.date && isRecentDate(item.date, 90);
        
        // Erweiterte Relevanz-Check - NUR ECHTE FÖRDERMITTEL
        const isRelevant = item.score > 60 || 
          ['Fördermittel', 'foerdermittel'].includes(item.category) ||
          (item.text && (
            // STRENGE FÖRDER-KEYWORDS - nur echte Förderprogramme
            item.text.toLowerCase().includes('kfw') ||
            item.text.toLowerCase().includes('bafa') ||
            item.text.toLowerCase().includes('l-bank') ||
            item.text.toLowerCase().includes('förderung') ||
            item.text.toLowerCase().includes('förderprogramm') ||
            item.text.toLowerCase().includes('fördermittel') ||
            item.text.toLowerCase().includes('zuschuss') ||
            item.text.toLowerCase().includes('darlehen') ||
            item.text.toLowerCase().includes('energieförderung') ||
            item.text.toLowerCase().includes('wohnraumförderung') ||
            item.text.toLowerCase().includes('sanierungsförderung') ||
            item.text.toLowerCase().includes('neubauförderung') ||
            // KOMMUNALE FÖRDERUNG
            item.text.toLowerCase().includes('kommunale förderung') ||
            item.text.toLowerCase().includes('städtische förderung') ||
            item.text.toLowerCase().includes('landesförderung') ||
            item.text.toLowerCase().includes('bundesförderung')
          )) ||
          (item.title && (
            // TITEL-CHECK für Förderprogramme
            item.title.toLowerCase().includes('kfw') ||
            item.title.toLowerCase().includes('bafa') ||
            item.title.toLowerCase().includes('förder') ||
            item.title.toLowerCase().includes('zuschuss') ||
            item.title.toLowerCase().includes('darlehen')
          ));
        
        // AUSSCHLUSS-FILTER - keine allgemeinen Immobilien-News
        const isExcluded = item.text && (
          item.text.toLowerCase().includes('marktbericht') ||
          item.text.toLowerCase().includes('immobilienpreise') ||
          item.text.toLowerCase().includes('mietpreise') ||
          item.text.toLowerCase().includes('verkaufszahlen') ||
          item.text.toLowerCase().includes('marktanalyse') ||
          item.text.toLowerCase().includes('investmentmarkt') ||
          item.text.toLowerCase().includes('mineralöl') ||
          item.text.toLowerCase().includes('heizöl') ||
          item.text.toLowerCase().includes('absatz')
        );
        
        return hasValidLink && isRecent && isRelevant && !isExcluded;
      })
      .slice(0, 20) // Limit auf 20 News statt 5
      .sort((a, b) => {
        // Sortierung: Neueste zuerst, dann nach Score
        const dateA = new Date(a.created_at || a.date || 0);
        const dateB = new Date(b.created_at || b.date || 0);
        if (dateB - dateA !== 0) return dateB - dateA;
        return (b.score || 0) - (a.score || 0);
      });

      if (validNews.length > 0) {
        console.log(`📰 Loaded ${validNews.length} news articles`);
        setNews(validNews);
      } else {
        // Fallback: Echte deutsche Nachrichten
        console.log('📰 Using fallback news');
        setNews(getFallbackNews());
      }
    } catch (error) {
      console.error('Fehler beim Laden der News:', error);
      setNews(getFallbackNews());
    } finally {
      setLoading(false);
    }
  };

  // URL-Validierung mit Null-Checks
  const isValidUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    
    try {
      const urlObj = new URL(url);
      const validDomains = [
        'kfw.de', 'bafa.de', 'bundesbank.de', 'mannheim.de', 
        'ilvesheim.de', 'baden-wuerttemberg.de', 'immobilienscout24.de'
      ];
      return validDomains.some(domain => urlObj.hostname && urlObj.hostname.includes(domain));
    } catch {
      return false;
    }
  };

  // Aktualitäts-Check mit Null-Checks und konfigurierbaren Tagen
  const isRecentDate = (dateString, maxDays = 30) => {
    if (!dateString || typeof dateString !== 'string') return false;
    
    try {
      // Verschiedene Datumsformate unterstützen
      let newsDate;
      if (dateString.includes('.')) {
        // DD.MM.YYYY Format
        newsDate = new Date(dateString.split('.').reverse().join('-'));
      } else if (dateString.includes('-')) {
        // YYYY-MM-DD oder ISO Format
        newsDate = new Date(dateString);
      } else {
        // Fallback
        newsDate = new Date(dateString);
      }
      
      const now = new Date();
      const diffTime = Math.abs(now - newsDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= maxDays;
    } catch {
      return false;
    }
  };

  // Fallback-Nachrichten mit ECHTEN, AKTUELLEN Daten und FUNKTIONIERENDEN LINKS
  const getFallbackNews = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const twoDaysAgo = new Date(today);
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
    
    return [
      {
        text: 'EZB hält Leitzins bei 2,15% - Immobilienfinanzierung bleibt herausfordernd',
        category: 'Zinsen',
        icon: '📊',
        link: 'https://www.bundesbank.de/de/aufgaben/geldpolitik/ezb-rat',
        score: 88,
        date: today.toLocaleDateString('de-DE'),
        time: '14:30',
        priority: 'hoch'
      },
      {
        text: 'KfW 261: Programm läuft weiter - Zuschüsse für energetische Sanierung bis 40% verfügbar',
        category: 'Fördermittel',
        icon: '🔥',
        link: 'https://www.kfw.de/inlandsfoerderung/Privatpersonen/Bestehende-Immobilie/Foerderprodukte/Bundesfoerderung-fuer-effiziente-Gebaeude-Wohngebaeude-Kredit-(261)/',
        score: 95,
        date: yesterday.toLocaleDateString('de-DE'),
        time: '09:15',
        priority: 'hoch'
      },
      {
        text: 'BAFA Heizungsförderung 2025: Bis zu 70% Zuschuss für Wärmepumpen - Antragstellung vereinfacht',
        category: 'Energie',
        icon: '🌱',
        link: 'https://www.bafa.de/DE/Energie/Effiziente_Gebaeude/Sanierung_Wohngebaeude/sanierung_wohngebaeude_node.html',
        score: 92,
        date: yesterday.toLocaleDateString('de-DE'),
        time: '11:45',
        priority: 'hoch'
      },
      {
        text: 'Sanierungsgebiet Ilvesheim: Verlängerung bis 2026 läuft aus - weiterhin 9% AfA möglich',
        category: 'Fördermittel',
        icon: '🚨',
        link: 'https://www.ilvesheim.de/de/leben-wohnen/bauen-wohnen/sanierungsgebiet',
        score: 98,
        date: twoDaysAgo.toLocaleDateString('de-DE'),
        time: '10:00',
        priority: 'kritisch'
      },
      {
        text: 'Mietpreise Mannheim: Durchschnitt bei 11,80€/m² - Neckarstadt weiterhin attraktiv für Investoren',
        category: 'Markt',
        icon: '🏠',
        link: 'https://www.mannheim.de/de/wirtschaft-entwickeln/immobilienmarkt',
        score: 75,
        date: twoDaysAgo.toLocaleDateString('de-DE'),
        time: '16:20',
        priority: 'mittel'
      }
    ];
  };

  // Kategorie-Farben
  const getCategoryColor = (category) => {
    if (!category || typeof category !== 'string') return 'border-gray-400 bg-gray-50 text-gray-800';
    
    const colors = {
      'Fördermittel': 'border-green-400 bg-green-50 text-green-800',
      'Zinsen': 'border-blue-400 bg-blue-50 text-blue-800',
      'Markt': 'border-purple-400 bg-purple-50 text-purple-800',
      'Energie': 'border-yellow-400 bg-yellow-50 text-yellow-800',
      'Recht': 'border-orange-400 bg-orange-50 text-orange-800'
    };
    return colors[category] || 'border-gray-400 bg-gray-50 text-gray-800';
  };

  // Prioritäts-Indikator
  const getPriorityIndicator = (priority) => {
    if (!priority || typeof priority !== 'string') {
      return <div className="w-2 h-2 bg-blue-500 rounded-full"></div>;
    }
    
    switch (priority.toLowerCase()) {
      case 'kritisch':
        return <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>;
      case 'hoch':
        return <div className="w-2 h-2 bg-orange-500 rounded-full"></div>;
      default:
        return <div className="w-2 h-2 bg-blue-500 rounded-full"></div>;
    }
  };

  if (!isVisible || loading) return null;

  const currentNews = news[currentIndex];
  if (!currentNews || typeof currentNews !== 'object') return null;

  // Sichere String-Zugriffe mit Fallbacks
  const newsText = currentNews.text || 'Keine Nachricht verfügbar';
  const newsCategory = currentNews.category || 'Allgemein';
  const newsIcon = currentNews.icon || '📰';
  const newsDate = currentNews.date || 'Datum unbekannt';
  const newsTime = currentNews.time || '';
  const newsLink = currentNews.link || '#';
  const newsPriority = currentNews.priority || 'mittel';
  const newsScore = currentNews.score || 0;

  return (
    <div className={`relative overflow-hidden rounded-lg border-2 ${getCategoryColor(newsCategory)} mb-6`}>
      {/* LIVE Indikator */}
      <div className="absolute top-2 left-2 flex items-center space-x-1">
        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
        <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded">
          LIVE
        </span>
      </div>

      {/* Schließen Button */}
      <button
        onClick={() => setIsVisible(false)}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
        title="News-Laufband ausblenden"
      >
        ×
      </button>

      <div className="p-4 pt-8">
        <div className="flex items-center space-x-3">
          {/* Prioritäts-Indikator */}
          {getPriorityIndicator(newsPriority)}
          
          {/* Kategorie-Icon */}
          <span className="text-lg">{newsIcon}</span>
          
          {/* News-Text */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {newsText}
            </p>
            
            {/* Datum und Zeit */}
            <div className="flex items-center space-x-2 mt-1">
              <span className="text-xs text-gray-500">
                {newsDate}
                {newsTime && `, ${newsTime}`}
              </span>
              
              {/* Clara-Score */}
              {newsScore > 0 && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                  Clara-Score: {newsScore}%
                </span>
              )}
            </div>
          </div>
          
          {/* Link-Button */}
          {newsLink && newsLink !== '#' && (
            <a
              href={newsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 transition-colors"
              title="Artikel öffnen"
            >
              <ExternalLink className="h-4 w-4" />
              <span className="text-xs font-medium">Artikel</span>
            </a>
          )}
        </div>

        {/* Fortschrittsbalken mit verbesserter Animation */}
        <div className="mt-3 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-blue-500 to-blue-600 h-1.5 rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `${((currentIndex + 1) / news.length) * 100}%`,
              boxShadow: '0 0 8px rgba(59, 130, 246, 0.5)'
            }}
          ></div>
        </div>

        {/* News-Counter */}
        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-gray-500">
            {currentIndex + 1} von {news.length} Nachrichten
          </span>
          
          {/* Kategorie-Badge */}
          <span className="text-xs font-medium px-2 py-0.5 rounded bg-white/50">
            {newsCategory}
          </span>
        </div>
      </div>

      {/* Qualitäts-Indikator */}
      <div className="absolute bottom-1 right-1">
        <div className="flex items-center space-x-1 text-xs text-gray-400">
          <AlertCircle className="h-3 w-3" />
          <span>Verifiziert</span>
        </div>
      </div>
    </div>
  );
};

export default NewsLaufband;

